import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useWsNavigate, WorkspaceLink } from "../hooks/useWsNavigate";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import AgentPicker from "../components/AgentPicker";
import useFetch from "../hooks/useFetch";
import { useCan } from "../hooks/useCan";
import {
  scheduledRunsAPI,
  scenariosAPI,
  notificationChannelsAPI,
  metricsAPI,
} from "../services/api";
import ScheduleBuilder from "../components/schedule/ScheduleBuilder";
import ConditionRow from "../components/conditions/ConditionRow";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import {
  Plus,
  Trash2,
  Clock,
  Play,
  Pause,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  Search,
  Pencil,
  Phone,
  Globe,
  MessageSquare,
  Bell,
  Slack,
  Mail,
  Webhook,
  X,
} from "lucide-react";

// ─── helpers ────────────────────────────────────────────────────────────────

// Icon for a notification channel by type.
const ChannelIcon = ({ type, className = "h-4 w-4" }) => {
  if (type === "slack") return <Slack className={className} />;
  if (type === "email") return <Mail className={className} />;
  if (type === "webhook") return <Webhook className={className} />;
  return <Bell className={className} />;
};

// Build a human-readable one-line summary of notification rules for a card.
const notificationSummary = (rules = [], channels = []) => {
  if (!rules || rules.length === 0) return "No notifications";
  const parts = rules.map((rule) => {
    const condPart =
      rule.conditions && rule.conditions.length > 0
        ? rule.conditions
            .slice(0, 1)
            .map((c) => {
              const val = c.value != null ? ` ${c.operator || ">"} ${c.value}` : "";
              return `condition${val}`;
            })
            .join(", ")
        : "any";
    const chNames = (rule.notification_channel_ids || [])
      .map((id) => {
        const ch = channels.find((c) => c.id === id);
        return ch ? ch.name : null;
      })
      .filter(Boolean);
    const chPart = chNames.length > 0 ? `→ ${chNames.join(", ")}` : "";
    return `${condPart} ${chPart}`.trim();
  });
  return `🔔 ${parts.join(" · ")}`;
};

// Stable-key counter — module-level so it survives re-renders but resets on
// full page reload (fine; keys only need to be stable within a session).
let _keyCounter = 0;
const nextKey = () => ++_keyCounter;

// Empty notification condition template.
const emptyCondition = () => ({
  _key: nextKey(),
  metric_id: null,
  metric_key: null,
  field: null,
  operator: "equals",
  value: "",
});

// Empty notification rule template.
const emptyRule = () => ({
  _key: nextKey(),
  name: "",
  conditions: [emptyCondition()],
  notification_channel_ids: [],
});

// ─── component ──────────────────────────────────────────────────────────────

const ScheduledRuns = ({ preSelectedScenarioIds = [], onScheduleCreated }) => {
  const { currentWorkspace } = useAuth();
  const { agents, selectedAgent, setSelectedAgent } = useAgent();
  const navigate = useWsNavigate();
  const { scheduleId } = useParams();
  const location = useLocation();
  const clientId = currentWorkspace?.id;
  const canWriteScheduledRun = useCan("scheduled_run", "write");
  const canWriteScheduleActivation = useCan("schedule_activation", "write");

  // The route drives create/edit, like /observability/notifications:
  //   /live-simulations/schedules            → list
  //   /live-simulations/schedules/new         → create form
  //   /live-simulations/schedules/:id/edit    → edit form
  const SCHEDULES_BASE = "/live-simulations/schedules";
  const mode = scheduleId
    ? "edit"
    : location.pathname.endsWith("/new")
    ? "new"
    : "list";
  const editorOpen = mode !== "list";
  const editingId = mode === "edit" ? Number(scheduleId) : null;
  // Which schedule id the form has already been populated from (so we don't
  // clobber the user's edits on every re-render).
  const populatedIdRef = useRef(null);

  // History expansion per card
  const [expandedId, setExpandedId] = useState(null);
  const [history, setHistory] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [scenarioSearch, setScenarioSearch] = useState("");

  // Notification support data
  const [channels, setChannels] = useState([]);
  const [metrics, setMetrics] = useState([]);

  const defaultForm = {
    name: "",
    cron_expression: "0 9 * * *",
    scenario_ids: [],
    testing_mode: "voice",
    voice_call_mode: "phone",
    channel: "phone",
    notification_rules: [],
  };

  const [form, setForm] = useState(defaultForm);

  // ── channel detection (same logic as NewBatchRun / live-simulations) ──────
  const legacyWebTypes = [
    "livekit_managed",
    "daily_managed",
    "websocket",
    "pipecat_livekit",
    "external_vapi",
    "external_retell",
  ];
  const phoneConfigured =
    !!selectedAgent?.phone_channel?.provider ||
    !!selectedAgent?.phone_number ||
    !!selectedAgent?.twilio_phone_number ||
    ["pipecat_twilio", "vapi"].includes(selectedAgent?.integration_type);
  const webConfigured =
    !!selectedAgent?.web_channel?.provider ||
    !!selectedAgent?.web_call_url ||
    legacyWebTypes.includes(selectedAgent?.integration_type);
  const textConfigured =
    !!selectedAgent?.text_channel?.provider ||
    !!selectedAgent?.text_config?.text_endpoint ||
    !!selectedAgent?.text_config?.text_websocket_url;

  // Map a channel choice to the form fields.
  const selectChannel = (ch) => {
    setForm((f) => {
      if (ch === "text")
        return { ...f, channel: "text", testing_mode: "text", voice_call_mode: null };
      if (ch === "phone")
        return { ...f, channel: "phone", testing_mode: "voice", voice_call_mode: "phone" };
      return { ...f, channel: "web", testing_mode: "voice", voice_call_mode: "web_call" };
    });
  };

  // Default channel when agent changes. Skipped while editing so the schedule's
  // saved channel is honored.
  useEffect(() => {
    if (mode === "edit") return;
    if (!selectedAgent?.id) return;
    const isConfigured =
      (form.channel === "phone" && phoneConfigured) ||
      (form.channel === "web" && webConfigured) ||
      (form.channel === "text" && textConfigured);
    if (isConfigured) return;
    if (phoneConfigured) selectChannel("phone");
    else if (webConfigured) selectChannel("web");
    else if (textConfigured) selectChannel("text");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent?.id]);

  // Reset scenario selection when selectedAgent changes (create flow only).
  // While editing, keep the schedule's saved scenarios even as we switch the
  // selected agent to match the schedule.
  useEffect(() => {
    if (mode === "edit") return;
    if (selectedAgent?.id) {
      setForm((f) => ({
        ...f,
        scenario_ids: preSelectedScenarioIds.length > 0 ? f.scenario_ids : [],
      }));
    }
  }, [selectedAgent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the create form with pre-selected scenarios (from the "Run on schedule"
  // flow on the scenarios tab).
  useEffect(() => {
    if (preSelectedScenarioIds.length > 0) {
      navigate(`${SCHEDULES_BASE}/new`);
      setForm((f) => ({ ...f, scenario_ids: preSelectedScenarioIds }));
    }
  }, [preSelectedScenarioIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load notification channels + metrics once clientId is ready.
  useEffect(() => {
    if (!clientId) return;
    notificationChannelsAPI.list(clientId).then((data) => setChannels(Array.isArray(data) ? data : [])).catch(() => {});
    metricsAPI.getAllMetrics(clientId).then((data) => {
      const all = [
        ...(data.standard_metrics || []),
        ...(data.global_metrics || []),
        ...(data.data || []),
      ];
      setMetrics(all);
    }).catch(() => {});
  }, [clientId]);

  // ── main data fetch ───────────────────────────────────────────────────────
  const {
    data: fetchResult,
    loading,
    refetch: fetchAll,
  } = useFetch(
    async () => {
      const [schedulesData, scenariosRes] = await Promise.all([
        scheduledRunsAPI.list(clientId),
        scenariosAPI.getScenarios(clientId),
      ]);
      return {
        schedules: schedulesData,
        scenarios: scenariosRes.data?.data || scenariosRes.data || [],
      };
    },
    [clientId],
    { enabled: !!clientId, initialData: { schedules: [], scenarios: [] } }
  );

  const schedules = fetchResult.schedules;
  const scenarios = fetchResult.scenarios;

  // ── helpers ───────────────────────────────────────────────────────────────
  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadHistory = async (id) => {
    if (history[id]) return;
    try {
      const data = await scheduledRunsAPI.history(clientId, id);
      setHistory((h) => ({ ...h, [id]: data }));
    } catch (e) {
      console.error("Failed to load history", e);
    }
  };

  const openCreate = () => navigate(`${SCHEDULES_BASE}/new`);
  const openEdit = (sr) => {
    setExpandedId(null);
    navigate(`${SCHEDULES_BASE}/${sr.id}/edit`);
  };
  const closeEditor = () => {
    setScenarioSearch("");
    onScheduleCreated?.();
    navigate(SCHEDULES_BASE);
  };

  // New mode → start from a blank form (seed with any pre-selected scenarios).
  useEffect(() => {
    if (mode === "list") populatedIdRef.current = null;
    if (mode === "new") {
      populatedIdRef.current = null;
      setForm({
        ...defaultForm,
        scenario_ids: preSelectedScenarioIds.length > 0 ? preSelectedScenarioIds : [],
      });
      setScenarioSearch("");
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode → populate the form from the schedule once the list has loaded,
  // and switch the selected agent so the channel + scenario list match it.
  useEffect(() => {
    if (mode !== "edit" || !editingId) return;
    if (populatedIdRef.current === editingId) return;
    const sr = schedules.find((s) => s.id === editingId);
    if (!sr) return; // list not loaded yet
    populatedIdRef.current = editingId;

    const ag = agents.find((a) => a.id === sr.agent_id);
    if (ag && ag.id !== selectedAgent?.id) setSelectedAgent(ag);

    setForm({
      name: sr.name,
      cron_expression: sr.cron_expression,
      scenario_ids: sr.scenario_ids || [],
      testing_mode: sr.testing_mode || "voice",
      voice_call_mode: sr.voice_call_mode || "phone",
      channel:
        sr.channel ||
        (sr.testing_mode === "text" ? "text" : sr.voice_call_mode === "web_call" ? "web" : "phone"),
      notification_rules: (sr.notification_rules || []).map((rule) => ({
        ...rule,
        _key: nextKey(),
        conditions: (rule.conditions || []).map((c) => ({ ...c, _key: nextKey() })),
      })),
    });
    setScenarioSearch("");
  }, [mode, editingId, schedules, agents]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) return flash("Name is required", "error");
    if (!selectedAgent?.id) return flash("Select an agent from the header", "error");
    if (form.scenario_ids.length === 0) return flash("Select at least one scenario", "error");
    if (!form.cron_expression.trim()) return flash("Schedule is required", "error");

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        agent_id: selectedAgent.id,
        cron_expression: form.cron_expression,
        scenario_ids: form.scenario_ids.map(Number),
        testing_mode: form.testing_mode,
        voice_call_mode: form.testing_mode === "voice" ? form.voice_call_mode : null,
        channel: form.channel,
        notification_rules: form.notification_rules.map(({ _key, conditions, ...rule }) => ({
          ...rule,
          conditions: conditions.map(({ _key: _ck, ...c }) => c),
        })),
      };

      if (editingId) {
        await scheduledRunsAPI.update(clientId, editingId, payload);
        flash("Schedule updated");
      } else {
        await scheduledRunsAPI.create(clientId, payload);
        flash("Schedule created");
      }
      fetchAll();
      closeEditor();
    } catch (e) {
      flash(e.response?.data?.errors?.join(", ") || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await scheduledRunsAPI.toggle(clientId, id);
      fetchAll();
    } catch (e) {
      flash(e.message, "error");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete scheduled run "${name}"?`)) return;
    try {
      await scheduledRunsAPI.destroy(clientId, id);
      flash(`"${name}" deleted`);
      fetchAll();
    } catch (e) {
      flash(e.message, "error");
    }
  };

  const toggleScenario = (scenarioId) => {
    setForm((f) => {
      const ids = f.scenario_ids.includes(scenarioId)
        ? f.scenario_ids.filter((id) => id !== scenarioId)
        : [...f.scenario_ids, scenarioId];
      return { ...f, scenario_ids: ids };
    });
  };

  // ── notification rule helpers ─────────────────────────────────────────────
  const addNotificationRule = () => {
    setForm((f) => ({ ...f, notification_rules: [...f.notification_rules, emptyRule()] }));
  };

  const removeNotificationRule = (ri) => {
    setForm((f) => ({ ...f, notification_rules: f.notification_rules.filter((_, i) => i !== ri) }));
  };

  const addConditionToRule = (ri) => {
    setForm((f) => {
      const rules = [...f.notification_rules];
      const rule = rules[ri];
      rules[ri] = { ...rule, conditions: [...rule.conditions, emptyCondition()] };
      return { ...f, notification_rules: rules };
    });
  };

  const changeConditionInRule = (ri, condIndex, updates) => {
    setForm((f) => {
      const rules = [...f.notification_rules];
      const rule = rules[ri];
      const conditions = [...rule.conditions];
      conditions[condIndex] = { ...conditions[condIndex], ...updates };
      rules[ri] = { ...rule, conditions };
      return { ...f, notification_rules: rules };
    });
  };

  const removeConditionFromRule = (ri, condIndex) => {
    setForm((f) => {
      const rules = [...f.notification_rules];
      const rule = rules[ri];
      rules[ri] = { ...rule, conditions: rule.conditions.filter((_, i) => i !== condIndex) };
      return { ...f, notification_rules: rules };
    });
  };

  const toggleChannelOnRule = (ri, channelId) => {
    setForm((f) => {
      const rules = [...f.notification_rules];
      const rule = rules[ri];
      const ids = rule.notification_channel_ids.includes(channelId)
        ? rule.notification_channel_ids.filter((id) => id !== channelId)
        : [...rule.notification_channel_ids, channelId];
      rules[ri] = { ...rule, notification_channel_ids: ids };
      return { ...f, notification_rules: rules };
    });
  };

  // ── derived lists ─────────────────────────────────────────────────────────
  const agentScenarios = selectedAgent?.id
    ? scenarios.filter((s) => !s.agent_id || s.agent_id === selectedAgent.id)
    : scenarios;

  const filteredAgentScenarios = agentScenarios.filter((s) =>
    s.name.toLowerCase().includes(scenarioSearch.toLowerCase())
  );

  const filteredSchedules = selectedAgent?.id
    ? schedules.filter((sr) => sr.agent_id === selectedAgent.id)
    : schedules;

  // ── loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── flash banner (shared by list + editor) ───────────────────────────────
  const flashBanner = message && (
    <div
      className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 mb-4 ${
        message.type === "error"
          ? "bg-destructive/10 text-destructive border border-destructive/20"
          : "bg-green-500/10 text-green-700 border border-green-500/20"
      }`}
    >
      {message.type === "error" ? (
        <XCircle className="h-4 w-4" />
      ) : (
        <CheckCircle2 className="h-4 w-4" />
      )}
      {message.text}
    </div>
  );

  // ── render: full-page editor ──────────────────────────────────────────────
  if (editorOpen) {
    return (
      <ScheduleEditor
        editingId={editingId}
        form={form}
        setForm={setForm}
        saving={saving}
        onSave={handleSave}
        onCancel={closeEditor}
        flashBanner={flashBanner}
        selectedAgent={selectedAgent}
        phoneConfigured={phoneConfigured}
        webConfigured={webConfigured}
        textConfigured={textConfigured}
        selectChannel={selectChannel}
        agentScenarios={agentScenarios}
        filteredAgentScenarios={filteredAgentScenarios}
        scenarioSearch={scenarioSearch}
        setScenarioSearch={setScenarioSearch}
        toggleScenario={toggleScenario}
        channels={channels}
        metrics={metrics}
        addNotificationRule={addNotificationRule}
        removeNotificationRule={removeNotificationRule}
        addConditionToRule={addConditionToRule}
        changeConditionInRule={changeConditionInRule}
        removeConditionFromRule={removeConditionFromRule}
        toggleChannelOnRule={toggleChannelOnRule}
      />
    );
  }

  // ── render: schedule list ─────────────────────────────────────────────────
  return (
    <div>
      <PageHeader description="Automatically run test scenarios on a recurring schedule.">
        {agents.length > 0 && (
          <AgentPicker
            agents={agents}
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
            onAdd={() => navigate("/ai-testing-agent/create")}
          />
        )}
        {canWriteScheduledRun && (
          <button
            onClick={openCreate}
            className="px-4 py-2.5 text-sm bg-foreground text-background rounded-lg hover:opacity-90 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> New Schedule
          </button>
        )}
      </PageHeader>

      {flashBanner}

      {/* ── Schedule list ─────────────────────────────────────────────── */}
      {filteredSchedules.length === 0 ? (
        <EmptyState
          compact
          icon={Clock}
          title="No Automated Schedules"
          description={
            selectedAgent?.id
              ? `No schedules found for ${selectedAgent.name}. Create one to automatically run scenarios.`
              : "Create a schedule to automatically run scenarios on a recurring basis."
          }
          primaryAction={
            canWriteScheduledRun
              ? { label: "New Schedule", onClick: openCreate }
              : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {filteredSchedules.map((sr) => {
            const summary = notificationSummary(sr.notification_rules, channels);
            return (
              <div
                key={sr.id}
                className="border border-border rounded-xl bg-card hover:border-input transition-colors"
              >
                {/* Card header row */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => {
                    setExpandedId(expandedId === sr.id ? null : sr.id);
                    loadHistory(sr.id);
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`h-2 w-2 rounded-full shrink-0 ${sr.enabled ? "bg-green-500" : "bg-muted-foreground/40"}`}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-foreground">{sr.name}</span>
                        {sr.agent_name && (
                          <span className="text-xs text-muted-foreground">{sr.agent_name}</span>
                        )}
                        {sr.channel && (
                          <span className="text-xs text-muted-foreground border border-border rounded px-1.5 py-0.5">
                            {sr.channel}
                          </span>
                        )}
                        {sr.scenario_ids?.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {sr.scenario_ids.length} scenario{sr.scenario_ids.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {sr.human_schedule ? (
                          <span className="text-xs text-muted-foreground">{sr.human_schedule}</span>
                        ) : (
                          <span className="text-xs font-mono text-muted-foreground">{sr.cron_expression}</span>
                        )}
                        {sr.next_run_at && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Next: {new Date(sr.next_run_at).toLocaleString()}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">{summary}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {canWriteScheduledRun && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(sr); }}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {canWriteScheduleActivation && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(sr.id); }}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                        title={sr.enabled ? "Pause" : "Resume"}
                      >
                        {sr.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                    )}
                    {canWriteScheduledRun && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(sr.id, sr.name); }}
                        className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-destructive/10"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {expandedId === sr.id ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Expanded: meta + run history */}
                {expandedId === sr.id && (
                  <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                    <div className="grid grid-cols-3 gap-4 text-sm text-muted-foreground">
                      <div>
                        <span className="font-medium text-foreground">Mode:</span>{" "}
                        {sr.testing_mode}
                        {sr.voice_call_mode && sr.testing_mode === "voice"
                          ? ` / ${sr.voice_call_mode}`
                          : ""}
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Scenarios:</span>{" "}
                        {sr.scenario_ids?.length || 0}
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Last run:</span>{" "}
                        {sr.last_run_at
                          ? new Date(sr.last_run_at).toLocaleString()
                          : "Never"}
                      </div>
                    </div>

                    {/* Run History */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Run History</p>
                      {!history[sr.id] ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                        </div>
                      ) : history[sr.id].length === 0 ? (
                        <p className="text-sm text-muted-foreground">No runs yet</p>
                      ) : (
                        <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                          {history[sr.id].map((batch) => (
                            <a
                              key={batch.id}
                              href={`/batch-run/${batch.id}`}
                              className="flex items-center justify-between px-3 py-2.5 hover:bg-muted text-sm"
                            >
                              <span className="text-foreground">{batch.name}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-muted-foreground">{batch.runs_count} runs</span>
                                <span
                                  className={`px-1.5 py-0.5 rounded text-xs ${
                                    batch.status === "completed"
                                      ? "bg-green-500/10 text-green-700"
                                      : batch.status === "failed"
                                      ? "bg-destructive/10 text-destructive"
                                      : "bg-amber-500/10 text-amber-700"
                                  }`}
                                >
                                  {batch.status}
                                </span>
                                <span className="text-muted-foreground">
                                  {new Date(batch.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── full-page editor ─────────────────────────────────────────────────────────
// 70/30 split: a single form card (Name → Channel → Scenarios → Schedule →
// completion notifications) on the left, a sticky live-summary + tips panel on
// the right. The notification block mirrors the observability alert editor.
const ScheduleEditor = ({
  editingId,
  form,
  setForm,
  saving,
  onSave,
  onCancel,
  flashBanner,
  selectedAgent,
  phoneConfigured,
  webConfigured,
  textConfigured,
  selectChannel,
  agentScenarios,
  filteredAgentScenarios,
  scenarioSearch,
  setScenarioSearch,
  toggleScenario,
  channels,
  metrics,
  addNotificationRule,
  removeNotificationRule,
  addConditionToRule,
  changeConditionInRule,
  removeConditionFromRule,
  toggleChannelOnRule,
}) => {
  const channelLabel =
    form.channel === "phone" ? "Phone" : form.channel === "web" ? "Web" : "Text";
  const totalNotifyChannels = form.notification_rules.reduce(
    (n, r) => n + (r.notification_channel_ids?.length || 0),
    0
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-base font-semibold text-foreground">
            {editingId ? "Edit Schedule" : "New Schedule"}
          </h1>
          <p className="ui-subtitle mt-0.5">Configure a recurring run and optional completion alerts.</p>
        </div>
        <button
          onClick={onCancel}
          className="px-3 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted flex items-center gap-2 shrink-0"
        >
          <X className="h-4 w-4" /> Back to schedules
        </button>
      </div>

      {flashBanner}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-6">
        {/* ── 70%: form card (observability-style sections) ──────────── */}
        <div className="border border-border rounded-lg bg-card self-start">
          {/* Subtitle bar */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm text-muted-foreground">
              Choose what runs and when, then optionally get alerted when it finishes.
            </p>
          </div>

          <div className="p-4 space-y-6">
            {/* ── Name ──────────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Nightly regression"
                className="w-full px-3 py-2 text-sm border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            {/* ── Channel ───────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Channel <span className="text-destructive">*</span>
              </label>
              {!selectedAgent?.id ? (
                <p className="text-sm text-muted-foreground">Select an agent from the header first</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "phone", label: "Phone", Icon: Phone, enabled: phoneConfigured },
                      { v: "web", label: "Web", Icon: Globe, enabled: webConfigured },
                      { v: "text", label: "Text", Icon: MessageSquare, enabled: textConfigured },
                    ].map(({ v, label, Icon, enabled }) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => enabled && selectChannel(v)}
                        disabled={!enabled}
                        title={enabled ? undefined : `${label} channel is not configured on this agent`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors
                          ${form.channel === v
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-input text-muted-foreground hover:border-muted-foreground"}
                          ${!enabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                  {!phoneConfigured && !webConfigured && !textConfigured && (
                    <p className="mt-2 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                      This agent has no channels configured. Edit the agent and enable Phone, Web, or Text.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* ── Scenarios ─────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Scenarios <span className="text-destructive">*</span>
                {form.scenario_ids.length > 0 && (
                  <span className="text-muted-foreground font-normal"> ({form.scenario_ids.length} selected)</span>
                )}
              </label>
              {!selectedAgent?.id ? (
                <p className="text-sm text-muted-foreground">Select an agent from the header first</p>
              ) : agentScenarios.length === 0 ? (
                <p className="text-sm text-muted-foreground">No scenarios found for this agent</p>
              ) : (
                <>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={scenarioSearch}
                      onChange={(e) => setScenarioSearch(e.target.value)}
                      placeholder="Search scenarios..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                    {filteredAgentScenarios.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.scenario_ids.includes(s.id)}
                          onChange={() => toggleScenario(s.id)}
                          className="h-4 w-4 rounded border-input"
                        />
                        <span className="text-sm text-foreground">{s.name}</span>
                      </label>
                    ))}
                    {filteredAgentScenarios.length === 0 && (
                      <p className="text-sm text-muted-foreground px-3 py-3">No scenarios match your search</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ── Schedule ──────────────────────────────────────────── */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Schedule <span className="text-destructive">*</span>
              </label>
              <ScheduleBuilder
                value={form.cron_expression}
                onChange={(cron) => setForm((f) => ({ ...f, cron_expression: cron }))}
              />
            </div>

            {/* ── When it finishes (notifications) ──────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground">
                  When it finishes <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <button
                  type="button"
                  onClick={addNotificationRule}
                  className="text-sm text-primary hover:opacity-80 flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Add notification
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Get notified when calls in the finished run match a condition — e.g. latency &gt; 4s.
              </p>

              {form.notification_rules.length === 0 ? (
                <p className="text-sm text-muted-foreground">No notifications configured.</p>
              ) : (
                <div className="space-y-6">
                  {form.notification_rules.map((rule, ri) => (
                    <div
                      key={rule._key}
                      className={ri > 0 ? "border-t border-border pt-6" : ""}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-foreground">
                          Notification {ri + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeNotificationRule(ri)}
                          className="text-muted-foreground hover:text-red-500"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Conditions */}
                      <p className="block text-sm font-medium text-foreground mb-2">
                        Conditions <span className="text-muted-foreground font-normal">(all must match)</span>
                      </p>
                      <div className="space-y-2">
                        {rule.conditions.map((cond, ci) => (
                          <ConditionRow
                            key={cond._key}
                            condition={cond}
                            index={ci}
                            metrics={metrics}
                            onChange={(idx, updates) => changeConditionInRule(ri, idx, updates)}
                            onRemove={rule.conditions.length > 1 ? (idx) => removeConditionFromRule(ri, idx) : undefined}
                          />
                        ))}
                      </div>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => addConditionToRule(ri)}
                          className="text-sm text-primary hover:opacity-80 flex items-center gap-1"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add condition
                        </button>
                      </div>

                      {/* Notify via */}
                      <p className="block text-sm font-medium text-foreground mt-4 mb-2">Notify via</p>
                      {channels.length === 0 ? (
                        <div className="bg-muted/50 border border-border rounded-md px-4 py-3 text-sm text-muted-foreground flex items-center justify-between">
                          <span>No notification channels configured yet.</span>
                          <WorkspaceLink to="/settings/integrations" className="text-foreground font-medium hover:underline">
                            Set up channels →
                          </WorkspaceLink>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {channels.map((ch) => {
                            const active = rule.notification_channel_ids.includes(ch.id);
                            return (
                              <button
                                key={ch.id}
                                type="button"
                                onClick={() => toggleChannelOnRule(ri, ch.id)}
                                className={`px-4 py-2 text-sm rounded-md border flex items-center gap-2 transition-colors ${
                                  active
                                    ? "bg-foreground border-foreground text-background"
                                    : "bg-card border-input text-foreground hover:border-muted-foreground"
                                }`}
                              >
                                <ChannelIcon type={ch.channel_type} className="h-4 w-4" />
                                <span>{ch.name}</span>
                                {active && <CheckCircle2 className="h-3.5 w-3.5" />}
                              </button>
                            );
                          })}
                          <WorkspaceLink
                            to="/settings/integrations"
                            className="px-4 py-2 text-sm rounded-md border border-dashed border-input text-muted-foreground hover:text-foreground hover:border-muted-foreground flex items-center gap-1"
                          >
                            <Plus className="h-3.5 w-3.5" /> Add channel
                          </WorkspaceLink>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer actions */}
          <div className="px-4 py-3 border-t border-border flex items-center gap-3">
            <button
              onClick={onSave}
              disabled={saving}
              className="px-4 py-2.5 text-sm bg-foreground text-background rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              {editingId ? "Save Changes" : "Create Schedule"}
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2.5 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* ── 30%: live summary + tips ───────────────────────────────── */}
        <div className="space-y-4 lg:sticky lg:top-6 self-start">
          {/* Summary card */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">Summary</h3>
            <dl className="space-y-2.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Name</dt>
                <dd className="text-foreground text-right truncate max-w-[60%]">
                  {form.name || "—"}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Agent</dt>
                <dd className="text-foreground text-right truncate max-w-[60%]">
                  {selectedAgent?.name || "—"}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Channel</dt>
                <dd className="text-foreground">{channelLabel}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Scenarios</dt>
                <dd className="text-foreground">{form.scenario_ids.length}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Notifications</dt>
                <dd className="text-foreground">
                  {form.notification_rules.length === 0
                    ? "Off"
                    : `${form.notification_rules.length} step${form.notification_rules.length !== 1 ? "s" : ""}`}
                  {totalNotifyChannels > 0 ? ` · ${totalNotifyChannels} channel${totalNotifyChannels !== 1 ? "s" : ""}` : ""}
                </dd>
              </div>
            </dl>
          </div>

          {/* Tips card */}
          <div className="bg-muted/40 border border-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" /> How schedules work
            </h3>
            <ul className="space-y-3 text-xs text-muted-foreground leading-relaxed">
              <li>
                <span className="font-medium text-foreground">Recurring runs.</span>{" "}
                Your selected scenarios run automatically on the channel above every
                time the trigger fires — no manual start needed.
              </li>
              <li>
                <span className="font-medium text-foreground">Workspace timezone.</span>{" "}
                Times you set in the trigger use your workspace's local timezone, so
                a 9:00 AM daily run always means 9:00 AM for your team.
              </li>
              <li>
                <span className="font-medium text-foreground">Smart alerts.</span>{" "}
                Notifications evaluate every call in the finished run. If, say,
                latency exceeds 4s on any calls, you'll be told exactly how many —
                delivered to Slack, email, or a webhook.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledRuns;
